/* eslint-disable no-param-reassign, no-use-before-define, @typescript-eslint/no-use-before-define, no-nested-ternary, no-continue, no-bitwise */
/**
 * Multi Internet: up to six connections at once, zero drops.
 *
 * Imperative port of the design's `initMI`. The six rows keep their slots (fibra, wifi, g5, cabo, isp2,
 * g4) but carry the providers of the visitor's country (the visitor's own ISP first when the answer has
 * it). Every figure on screen derives from the connection states in one pass (`syncDerived`), so the
 * proof card, the result row, the node captions, the backup card and the screen-reader line can never
 * disagree. An auto demo tells the story (pair, +1, a drop, the failover, the recovery) until the
 * visitor touches a card; then it only replays the failover against the visitor's own set. Packets run
 * on WAAPI along wires generated here (on phones, one vertical bus). `start` returns the teardown.
 */
import type { GeoData } from './geo';
import { escapeHtml, foldText, forcedGeoParams, requestVisitorGeo } from './geo';
import type { Translate } from './runtime';
import { createRuntime, one, round1 } from './runtime';

export interface MultiInternetOptions {
  geo: GeoData;
  translate: Translate;
  joinList: (items: string[]) => string;
  formatNumber: (value: number, fractionDigits: number) => string;
}

type ConnectionId = 'fibra' | 'wifi' | 'g5' | 'cabo' | 'isp2' | 'g4';

interface Segment {
  d: string;
  parks: number[];
  packets: PacketSlot[];
  frames: Keyframe[] | null;
  parkIdx: number[] | null;
  hide?: HideRect[] | null;
  len?: number;
}

interface PacketSlot {
  el: HTMLDivElement;
  phase: number;
  anim: Animation | null;
}

interface HideRect {
  l: number;
  t: number;
  r: number;
  b: number;
}

interface Box {
  l: number;
  t: number;
  r: number;
  b: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

interface Connection {
  id: ConnectionId;
  mbps: number;
  name: string;
  brand: string;
  kind: string;
  sr: string;
  el: HTMLButtonElement;
  ccEl: HTMLElement;
  tagEl: HTMLElement;
  mbEl: HTMLElement;
  deltaEl: HTMLElement;
  spLine: SVGPolylineElement;
  spFill: SVGPolygonElement;
  spDot: SVGCircleElement;
  on: boolean;
  down: boolean;
  wasOn: boolean;
  wasDown?: boolean;
  offT: number;
  hist: number[];
  g?: SVGGElement;
  pg?: SVGGElement;
  set?: HTMLDivElement;
  baseIn?: SVGPathElement;
  baseOut?: SVGPathElement;
  glowIn?: SVGPathElement;
  glowOut?: SVGPathElement;
  hotIn?: SVGPathElement;
  hotOut?: SVGPathElement;
  cut?: SVGGElement;
  portIn?: SVGCircleElement;
  portOut?: SVGCircleElement;
  segIn?: Segment;
  segOut?: Segment;
  stubD?: string;
  cutXY?: string;
  busR?: { x: number; tile: Box; card: Box } | null;
}

interface Trunk extends Segment {
  g: SVGGElement | null;
  set: HTMLDivElement | null;
  base: SVGPathElement | null;
  glow: SVGPathElement | null;
  hot: SVGPathElement | null;
  wasOn: boolean | null;
  offT: number;
  live: number;
}

interface StoryStep {
  wait: number;
  run: () => void;
}

const NS = 'http://www.w3.org/2000/svg';
const SLOTS: readonly { id: ConnectionId; mbps: number }[] = [
  { id: 'fibra', mbps: 500 },
  { id: 'wifi', mbps: 300 },
  { id: 'g5', mbps: 150 },
  { id: 'cabo', mbps: 250 },
  { id: 'isp2', mbps: 200 },
  { id: 'g4', mbps: 60 },
];
const SP_W = 52;
const SP_H = 18;
const HIST_N = 8;
const BASE_MS = 1600;
const TRUNK_PHASES_DESK = [0.12, 0.55];
const TRUNK_PHASES_BUS = [0.03, 0.3, 0.5, 0.82];
const BEND_A = 0.15;
const BEND_B = 0.27;
const PORT_D = 5.25;
const JACK_D = 4;
const USER_IDLE = 8000;

function box(rect: DOMRect, stage: DOMRect): Box {
  return { l: rect.left - stage.left, t: rect.top - stage.top, r: rect.right - stage.left, b: rect.bottom - stage.top, w: rect.width, h: rect.height, cx: rect.left - stage.left + rect.width / 2, cy: rect.top - stage.top + rect.height / 2 };
}

function shiftBox(rect: Box, dy: number): Box {
  return { ...rect, t: rect.t + dy, b: rect.b + dy, cy: rect.cy + dy };
}

/* card -> NoPing socket: early bend, flat arrival (the steep stretch stays beside the cards) */
function cubicRight(x1: number, y1: number, x2: number, y2: number): string {
  const dx = x2 - x1;
  const first = Math.min(Math.max(dx * BEND_A, 10), dx * 0.45);
  return `C${round1(x1 + first)},${round1(y1)} ${round1(x1 + Math.max(dx * BEND_B, first + 6))},${round1(y2)} ${round1(x2)},${round1(y2)}`;
}

/* PC socket -> card jack: flat departure, late bend (mirror of cubicRight) */
function cubicLeft(x1: number, y1: number, x2: number, y2: number): string {
  const dx = x2 - x1;
  return `C${round1(x2 - dx * BEND_B)},${round1(y1)} ${round1(x2 - dx * BEND_A)},${round1(y2)} ${round1(x2)},${round1(y2)}`;
}

/* the severed stub: red dashes up to `fraction` of the card -> NoPing stroke, then nothing */
function cutDash(fraction: number): string {
  const out: number[] = [];
  let position = 0;
  const end = fraction * 100;
  while (position + 3 <= end) {
    out.push(3, 3.5);
    position += 6.5;
  }
  out.push(0, 100);
  return out.join(' ');
}

interface Polyline {
  pts: { x: number; y: number; s: number }[];
  len: number;
}

/* path geometry in plain JS: the paths are generated here (absolute M / L / C only), so lengths and points
   never touch SVG layout (no getTotalLength, no forced reflow) */
function samplePath(d: string): Polyline {
  const tokens = d.match(/[MLC]|-?\d*\.?\d+/g) ?? [];
  let index = 0;
  const pts: Polyline['pts'] = [];
  let total = 0;
  let current: { x: number; y: number } | null = null;
  let command = '';
  const read = () => {
    const value = parseFloat(tokens[index] ?? '0');
    index += 1;
    return value;
  };
  const push = (x: number, y: number, jump: boolean) => {
    if (current && !jump) total += Math.hypot(x - current.x, y - current.y);
    current = { x, y };
    pts.push({ x, y, s: total });
  };
  while (index < tokens.length) {
    const token = tokens[index];
    if (token === 'M' || token === 'L' || token === 'C') {
      command = token;
      index += 1;
      continue;
    }
    if (command === 'M') {
      push(read(), read(), true);
      command = 'L';
    } else if (command === 'L') push(read(), read(), false);
    else if (command === 'C' && current) {
      const { x: x0, y: y0 } = current as { x: number; y: number };
      const x1 = read();
      const y1 = read();
      const x2 = read();
      const y2 = read();
      const x3 = read();
      const y3 = read();
      for (let step = 1; step <= 24; step += 1) {
        const u = step / 24;
        const v = 1 - u;
        push(v * v * v * x0 + 3 * v * v * u * x1 + 3 * v * u * u * x2 + u * u * u * x3, v * v * v * y0 + 3 * v * v * u * y1 + 3 * v * u * u * y2 + u * u * u * y3, false);
      }
    } else index += 1;
  }
  return { pts, len: total };
}

function pointAt(poly: Polyline, distance: number): { x: number; y: number } {
  const { pts } = poly;
  let low = 0;
  let high = pts.length - 1;
  const first = pts[0];
  if (!first) return { x: 0, y: 0 };
  if (distance <= 0) return first;
  if (distance >= poly.len) return pts[high] ?? first;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if ((pts[middle]?.s ?? 0) <= distance) low = middle;
    else high = middle;
  }
  const from = pts[low] ?? first;
  const to = pts[high] ?? first;
  const span = to.s - from.s;
  const ratio = span > 0 ? (distance - from.s) / span : 0;
  return { x: from.x + (to.x - from.x) * ratio, y: from.y + (to.y - from.y) * ratio };
}

function inRects(point: { x: number; y: number }, rects: HideRect[]): boolean {
  return rects.some((rect) => point.x >= rect.l && point.x <= rect.r && point.y >= rect.t && point.y <= rect.b);
}

export function startMultiInternet(root: HTMLElement, options: MultiInternetOptions): () => void {
  const { geo, translate, joinList, formatNumber } = options;
  const t = (key: string, values?: Record<string, unknown>) => translate(`mi.${key}`, { ...values, interpolation: { escapeValue: false } });
  const runtime = createRuntime();
  const { later, clearLater, every, clearEvery, nextFrame, cancelFrame, listen } = runtime;

  root.classList.add('is-js');
  const stage = one<HTMLElement>(root, '[data-stage]');
  const svg = one<SVGSVGElement>(root, '[data-wires]');
  const packetLayer = one<HTMLElement>(root, '[data-packets]');
  const list = one<HTMLElement>(root, '[data-routes]');
  const pcNode = one<HTMLElement>(root, '[data-node="pc"]');
  const npNode = one<HTMLElement>(root, '[data-node="np"]');
  const gameNode = one<HTMLElement>(root, '[data-node="game"]');
  const pcIcon = one<HTMLElement>(pcNode, '.mi-node-icon');
  const npIcon = one<HTMLElement>(npNode, '.mi-node-icon');
  const gameIcon = one<HTMLElement>(gameNode, '.mi-node-icon');
  const npSub = one<HTMLElement>(root, '[data-np-sub]');
  const gameSub = one<HTMLElement>(root, '[data-game-sub]');
  const statusText = one<HTMLElement>(root, '[data-status-text]');
  const liveEl = one<HTMLElement>(root, '[data-live]');
  const bridge = one<HTMLElement>(root, '[data-bridge]');
  const dropButton = one<HTMLButtonElement>(root, '[data-drop]');
  const dropLabel = one<HTMLElement>(dropButton, '[data-lbl]');
  const pauseButton = one<HTMLButtonElement>(root, '[data-pause]');
  const hintText = one<HTMLElement>(root, '[data-hint]');
  const keyEl = one<HTMLElement>(root, '[data-key]');
  const countEl = one<HTMLElement>(root, '[data-count]');
  const countWord = one<HTMLElement>(root, '[data-count-w]');
  const backupSub = one<HTMLElement>(root, '[data-backup-sub]');
  const backupHeadline = one<HTMLElement>(root, '[data-backup-b]');
  const proofBase = one<HTMLElement>(root, '[data-p-base]');
  const proofBaseUnit = one<HTMLElement>(root, '[data-p-base-u]');
  const proofSum = one<HTMLElement>(root, '[data-p-sum]');
  const proofSumUnit = one<HTMLElement>(root, '[data-p-sum-u]');
  const proofPct = one<HTMLElement>(root, '[data-p-pct]');
  const proofFill = one<HTMLElement>(root, '[data-p-fill]');
  const proofFillGood = one<HTMLElement>(root, '[data-p-fill-good]');
  const proofDrops = one<HTMLElement>(root, '[data-p-drops]');
  const proofSr = root.querySelector<HTMLElement>('[data-proof-sr]');
  const sumEl = one<HTMLElement>(root, '[data-sum]');
  const sumUnit = one<HTMLElement>(root, '[data-sum-u]');
  const sumStrong = sumEl.parentElement as HTMLElement;
  const sumHeadline = one<HTMLElement>(root, '[data-sum-b]');
  const sumSub = one<HTMLElement>(root, '[data-sum-sub]');
  const sumRow = one<HTMLElement>(root, '[data-sum-row]');
  const srNow = root.querySelector<HTMLElement>('[data-sr-now]');
  const miDesc = root.querySelector<HTMLElement>('[data-mi-desc]');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supportsMotion = typeof packetLayer.animate === 'function';
  const isMobile = () => getComputedStyle(stage).getPropertyValue('--mi-mobile').trim() === '1';

  /* the match's drop counter goes to 1 only when a lone connection fails (no reserve), and stays there
     until a second connection is active: the row never contradicts its own caption */
  let kicked = false;
  let resultNote: string | null = null;
  const BACKUP_REST: [string, string] = [t('backup.rest'), t('backup.rest_sub')];
  const BACKUP_HOT: [string, string] = [t('backup.hot'), t('backup.hot_sub')];
  const BACKUP_LONE: [string, string] = [t('backup.lone'), t('backup.lone_sub')];
  const BACKUP_NONE: [string, string] = [t('backup.none'), t('backup.none_sub')];

  /* product terms never break across lines ("Wi-Fi 6", "5G do celular") */
  const nb = (text: string) => `<span class="nb">${escapeHtml(text)}</span>`;
  /* long / short wording, picked by the same container branch as the key label */
  const alt = (long: string, short: string) => `<span class="x-long">${long}</span><span class="x-short">${short}</span>`;
  const capacity = (mbps: number) => {
    if (!mbps) return { n: '-', u: '', sr: t('cap.none') };
    if (mbps < 1000) return { n: String(mbps), u: 'Mb/s', sr: t('cap.mbps_sr', { value: mbps }) };
    const gigabits = formatNumber(Math.round(mbps / 10) / 100, 2);
    return { n: gigabits, u: 'Gb/s', sr: t('cap.gbps_sr', { value: gigabits }) };
  };

  /* ------------------------------------------------ the visitor's providers */
  const PROVIDERS = geo.providers;
  const kindLabel = (kind: string) => t(`kind.${kind}`);
  const normalizeGeo = (data: unknown) => {
    if (!data || typeof data !== 'object') return null;
    const record = data as Record<string, unknown>;
    const countryField = record.country;
    const code = record.country_code ?? record.countryCode ?? (countryField && typeof countryField === 'object' ? (countryField as Record<string, unknown>).code : countryField);
    if (!code || String(code).length !== 2) return null;
    const connection = record.connection as Record<string, unknown> | undefined;
    const isp = record.isp ?? record.org ?? record.asn_org ?? connection?.isp ?? connection?.org ?? '';
    return { country: String(code).toUpperCase(), isp: String(isp) };
  };
  const forced = normalizeGeo(forcedGeoParams(window.location.search));

  interface ProviderPick {
    brand: string;
    kind: string;
    mine?: boolean;
  }
  const pickProviders = (countryCode: string, isp: string): Partial<Record<ConnectionId, ProviderPick>> => {
    const providers = PROVIDERS[countryCode] ?? PROVIDERS.US ?? {};
    const used = new Set<string>();
    const fixed = providers.fixed ?? [];
    const mobile = providers.mobile ?? [];
    type Entry = [string, string] | [string, boolean];
    const take = (pool: Entry[], accept?: (entry: Entry) => boolean) => {
      const found = pool.find((entry) => !used.has(foldText(entry[0], '&')) && (!accept || accept(entry)));
      if (found) used.add(foldText(found[0], '&'));
      return found ?? null;
    };
    const any = (pool: Entry[], index: number) => (pool.length ? (pool[index % pool.length] ?? null) : null);
    let mine: Entry | null = null;
    const folded = foldText(isp, '&');
    if (folded) {
      [...fixed, ...mobile].forEach((entry: Entry) => {
        const key = foldText(entry[0], '&');
        if (!mine && key && (folded.indexOf(key) >= 0 || key.indexOf(folded) >= 0)) mine = entry;
      });
      if (mine) used.add(foldText((mine as Entry)[0], '&'));
    }
    const isMobileEntry = (entry: Entry) => (mobile as Entry[]).includes(entry);
    const kindOf = (entry: Entry, wifi?: boolean) => (isMobileEntry(entry) ? (entry[1] === true ? '5G' : '4G') : wifi ? 'Wi-Fi 6' : kindLabel(String(entry[1]) || 'fiber'));
    const fibre = (mine ?? take(fixed, (entry) => entry[1] === 'fiber') ?? take(fixed) ?? any(fixed, 0) ?? any(mobile, 0)) as Entry | null;
    if (!fibre) return {};
    const out: Partial<Record<ConnectionId, ProviderPick>> = {};
    out.fibra = { brand: fibre[0], kind: kindOf(fibre), mine: !!mine };
    const cable = (take(fixed, (entry) => entry[1] === 'cable') ?? take(fixed) ?? any(fixed, 1) ?? fibre) as Entry;
    out.cabo = { brand: cable[0], kind: kindOf(cable) };
    const second = (take(fixed) ?? any(fixed, 2) ?? cable) as Entry;
    out.isp2 = { brand: second[0], kind: kindOf(second) };
    const wifi = (take(fixed) ?? any(fixed, 3) ?? fibre) as Entry;
    out.wifi = { brand: wifi[0], kind: isMobileEntry(wifi) ? kindOf(wifi) : 'Wi-Fi 6' };
    /* mobile rows: a brand already on a fixed row is fine ("Vivo Fibra" + "Vivo 4G"), the same operator twice is not */
    const fiveG = (take(mobile, (entry) => entry[1] === true) ?? take(mobile) ?? any(mobile, 0) ?? fibre) as Entry;
    out.g5 = { brand: fiveG[0], kind: isMobileEntry(fiveG) && fiveG[1] === true ? '5G' : '4G' };
    const fourG = (take(mobile) ?? mobile.find((entry) => entry !== fiveG) ?? any(mobile, 1) ?? fiveG) as Entry;
    out.g4 = { brand: fourG[0], kind: '4G' };
    return out;
  };

  const connections: Connection[] = SLOTS.map((slot) => {
    const el = one<HTMLButtonElement>(list, `[data-conn="${slot.id}"]`);
    return {
      id: slot.id,
      mbps: slot.mbps,
      name: '',
      brand: '',
      kind: '',
      sr: '',
      el,
      ccEl: one<HTMLElement>(el, '.mi-route-cc'),
      tagEl: one<HTMLElement>(el, '[data-tag]'),
      mbEl: one<HTMLElement>(el, '[data-mb]'),
      deltaEl: one<HTMLElement>(el, '[data-delta]'),
      spLine: one<SVGPolylineElement>(el, '.mi-route-spark polyline'),
      spFill: one<SVGPolygonElement>(el, '.mi-route-spark .sp-fill'),
      spDot: one<SVGCircleElement>(el, '.mi-route-spark .sp-dot'),
      on: el.classList.contains('is-on'),
      down: false,
      wasOn: el.classList.contains('is-on'),
      offT: 0,
      hist: [],
    };
  });
  const TOTAL = connections.length;
  const applyProviders = (selection: Partial<Record<ConnectionId, ProviderPick>>) => {
    const count = new Map<string, number>();
    connections.forEach((connection) => {
      const pick = selection[connection.id];
      if (pick) count.set(pick.brand, (count.get(pick.brand) ?? 0) + 1);
    });
    connections.forEach((connection) => {
      const pick = selection[connection.id];
      if (!pick) return;
      connection.brand = pick.brand;
      connection.kind = pick.mine ? t('kind.mine', { kind: pick.kind }) : pick.kind;
      connection.name = (count.get(pick.brand) ?? 0) > 1 ? `${pick.brand} ${pick.kind}` : pick.brand;
      connection.sr = pick.mine ? t('kind.mine_sr', { brand: pick.brand, kind: pick.kind }) : `${pick.brand} ${pick.kind}`;
    });
  };
  applyProviders(pickProviders(forced?.country ?? 'US', forced?.isp ?? ''));

  /* ---------------------------------------------- throughput sparkline */
  const sparkLevel = (connection: Connection) => (connection.down ? 0 : connection.on ? 0.78 : 0.14);
  const sparkSample = (connection: Connection) => {
    const level = sparkLevel(connection);
    if (!level) return 0;
    const amplitude = connection.on ? 0.16 : 0.05;
    return Math.max(0.04, Math.min(0.96, level + (Math.random() * 2 - 1) * amplitude));
  };
  const seedHistory = (connection: Connection) => {
    connection.hist = [];
    for (let index = 0; index < HIST_N; index += 1) connection.hist.push(sparkSample(connection));
  };
  const drawSpark = (connection: Connection) => {
    const count = connection.hist.length;
    const points = connection.hist.map((value, index) => `${round1(1.5 + ((SP_W - 3) * index) / (HIST_N - 1))},${round1(SP_H - 1.5 - (SP_H - 3) * value)}`);
    connection.spLine.setAttribute('points', points.join(' '));
    connection.spFill.setAttribute('points', `1.5,${SP_H} ${points.join(' ')} ${round1(1.5 + ((SP_W - 3) * (count - 1)) / (HIST_N - 1))},${SP_H}`);
    const last = (points[count - 1] ?? '0,0').split(',');
    connection.spDot.setAttribute('cx', last[0] ?? '0');
    connection.spDot.setAttribute('cy', last[1] ?? '0');
  };
  const pushSample = (connection: Connection) => {
    connection.hist.push(sparkSample(connection));
    if (connection.hist.length > HIST_N) connection.hist.shift();
    drawSpark(connection);
  };
  let sparkTimer = 0;
  const sparksRun = (on: boolean) => {
    clearEvery(sparkTimer);
    sparkTimer = 0;
    if (on) sparkTimer = every(() => connections.forEach(pushSample), 640);
  };
  connections.forEach((connection) => {
    seedHistory(connection);
    drawSpark(connection);
  });

  const byId = (id: string | null) => connections.find((connection) => connection.id === id) ?? null;
  const active = () => connections.filter((connection) => connection.on && !connection.down);
  const comboText = (activeConnections: Connection[]) => t('combo', { count: activeConnections.length });
  const labelFor = (connection: Connection) => {
    const base = { name: connection.sr, mbps: connection.mbps };
    if (connection.down) return active().length ? t('label.down_covered', base) : t('label.down_alone', base);
    return connection.on ? t('label.on', base) : t('label.reserve', base);
  };
  const paintConnections = () => {
    connections.forEach((connection) => {
      const title = connection.el.querySelector<HTMLElement>('.mi-route-city');
      const kind = connection.el.querySelector<HTMLElement>('.mi-route-kind');
      /* the sub-line already says the kind: the title is the brand alone */
      if (title) title.textContent = kind ? connection.brand : `${connection.brand} ${connection.kind}`;
      if (kind) kind.textContent = connection.kind;
      connection.el.setAttribute('aria-label', labelFor(connection));
    });
    if (miDesc) miDesc.textContent = t('description', { connections: joinList(connections.map((connection) => connection.sr)) });
  };
  paintConnections();

  /* ---------------------------------------------------------------- state */
  let paused = false;
  let dropping = false;
  let visible = true;
  let running = false;
  let built = false;
  let hoverId: string | null = null;
  let stepTimer = 0;
  let dropTimer = 0;
  const boost = 1;
  const rateFor = (connection: Connection) => (BASE_MS / (1500 - connection.mbps * 0.9)) * boost;

  /* one-shot effects are restarted through one shared frame: drop the class now, put it back next frame */
  let pulseQueue: { el: Element; cls: string }[] = [];
  let pulseRaf = 0;
  const pulseFlush = () => {
    pulseRaf = 0;
    const queue = pulseQueue;
    pulseQueue = [];
    queue.forEach((entry) => entry.el.classList.add(entry.cls));
  };
  const pulse = (element: Element | null | undefined, className: string) => {
    if (reduced.matches || !element) return;
    if (!element.classList.contains(className)) {
      element.classList.add(className);
      return;
    }
    element.classList.remove(className);
    if (pulseQueue.some((entry) => entry.el === element && entry.cls === className)) return;
    pulseQueue.push({ el: element, cls: className });
    if (!pulseRaf) pulseRaf = nextFrame(pulseFlush);
  };
  const flip = (element: Element) => pulse(element, 'is-flip');
  const swapText = (element: HTMLElement, text: string, hot?: boolean) => {
    if (element.textContent === text && (hot === undefined || element.classList.contains('is-failover') === !!hot)) return;
    element.textContent = text;
    if (hot !== undefined) element.classList.toggle('is-failover', !!hot);
    pulse(element, 'is-swap');
  };

  const trunk: Trunk = { g: null, set: null, base: null, glow: null, hot: null, packets: [], frames: null, parkIdx: null, d: '', parks: [0.22, 0.7], wasOn: null, offT: 0, len: 0, hide: null, live: 2 };
  let wasKey = '';
  let busMode = false;
  let busGeometry: { x: number; pcB: number; npT: number; npB: number; gmT: number; tiles: Box[]; rects: Box[] } | null = null;
  let busKey = '';
  const ports: Partial<Record<'pcOut' | 'npIn' | 'npOut' | 'gameIn', SVGCircleElement>> = {};
  const tileMask: { el: SVGMaskElement | null; all: SVGRectElement | null; rects: SVGRectElement[] } = { el: null, all: null, rects: [] };
  let hintGone = false;

  /* every figure on screen derives from the connections in this one pass */
  const syncDerived = () => {
    const activeConnections = active();
    const any = activeConnections.length > 0;
    const anyDown = connections.some((connection) => connection.down);
    const sum = activeConnections.reduce((total, connection) => total + connection.mbps, 0);
    const sumCapacity = capacity(sum);
    const keyNow = `${activeConnections.length}|${any ? 1 : 0}`;
    countEl.textContent = String(activeConnections.length);
    countWord.textContent = activeConnections.length === 1 ? t('count_word.one') : t('count_word.other');
    if (wasKey && keyNow !== wasKey) flip(keyEl);
    wasKey = keyNow;
    connections.forEach((connection) => {
      const on = connection.on && !connection.down;
      const { down } = connection;
      const changed = on !== connection.wasOn || down !== connection.wasDown;
      if (down !== connection.wasDown && connection.wasDown !== undefined) snap([connection.el, connection.g, connection.pg]);
      connection.el.classList.toggle('is-on', on);
      connection.el.classList.toggle('is-down', down);
      connection.el.classList.toggle('is-off', !on && !down);
      connection.el.setAttribute('aria-pressed', on ? 'true' : 'false');
      connection.el.setAttribute('aria-label', labelFor(connection));
      connection.tagEl.textContent = down ? t('tag.down') : on ? t('tag.on') : t('tag.reserve');
      connection.mbEl.textContent = down ? '0' : String(connection.mbps);
      connection.deltaEl.textContent = down ? t('delta.down') : on ? t('delta.on') : t('delta.reserve');
      if (connection.g && connection.pg) {
        connection.g.classList.toggle('is-on', on);
        connection.g.classList.toggle('is-down', down);
        connection.g.classList.toggle('is-off', !on && !down);
        connection.pg.classList.toggle('is-on', on);
        connection.pg.classList.toggle('is-down', down);
        if (down !== connection.wasDown) syncStub(connection);
      }
      connection.set?.classList.toggle('is-on', on && !busMode);
      if (changed) {
        /* the sparkline reacts at once: a drop plunges to zero, a (re)entry ramps up over the next ticks */
        if (sparkTimer) pushSample(connection);
        else {
          seedHistory(connection);
          drawSpark(connection);
        }
        if (connection.segIn) setPlay(connection, on);
      }
      connection.wasOn = on;
      connection.wasDown = down;
    });
    if (busMode) updateBusTrunk();
    if (trunk.g) {
      trunk.g.classList.toggle('is-on', any);
      trunk.g.classList.toggle('is-off', !any);
    }
    if (trunk.set) {
      trunk.set.classList.toggle('is-on', any);
      if (any !== trunk.wasOn) {
        setPlay(trunk, any);
        trunk.wasOn = any;
      }
    }
    if (ports.npIn) {
      [ports.pcOut, ports.npIn, ports.npOut, ports.gameIn].forEach((port) => port?.classList.toggle('is-hot', any));
    }
    root.classList.toggle('is-offline', !any);
    /* ONE connection carrying the game is the no-reserve state; the only exception is the failover beat
       being told ("caiu, a outra assumiu") */
    const bad = anyDown && !any;
    const noReserve = activeConnections.length === 1;
    const telling = anyDown && any && !!resultNote;
    const lone = noReserve && !telling;
    if (activeConnections.length >= 2) kicked = false;
    const counted = bad || (kicked && noReserve);
    root.classList.toggle('is-weak', lone);
    /* the proof card: "uma internet só" is the fastest active link, "Com Multi Internet" all of them combined */
    const peak = activeConnections.reduce((best, connection) => Math.max(best, connection.mbps), 0);
    const baseCapacity = capacity(peak);
    const pctGain = peak ? Math.round(((sum - peak) / peak) * 100) : 0;
    proofBase.textContent = peak ? baseCapacity.n : '0';
    proofBaseUnit.textContent = peak ? baseCapacity.u : 'Mb/s';
    proofSum.textContent = sum ? sumCapacity.n : '0';
    proofSumUnit.textContent = sum ? sumCapacity.u : 'Mb/s';
    proofPct.textContent = `+${pctGain}%`;
    proofPct.style.visibility = pctGain > 0 ? '' : 'hidden';
    const widthBad = sum ? Math.round((peak / sum) * 100) : 0;
    const widthGood = sum ? 100 : 0;
    proofFill.style.setProperty('--mi-fill', `${widthBad}%`);
    proofFill.classList.toggle('is-empty', !widthBad);
    proofFillGood.style.setProperty('--mi-fill', `${widthGood}%`);
    proofFillGood.classList.toggle('is-empty', !widthGood);
    proofDrops.textContent = counted ? t('drops.one') : t('drops.none');
    proofDrops.classList.toggle('is-bad', counted);
    if (proofSr) proofSr.textContent = t('proof_sr', { base: peak ? baseCapacity.sr : t('cap.none'), sum: sum ? sumCapacity.sr : t('cap.none'), gain: pctGain > 0 ? t('proof_sr_gain', { pct: pctGain }) : '', drops: counted ? t('drops.one') : t('drops.none') });
    /* the backup card and the trunk glow are held for the whole outage */
    const failover = anyDown && any && !lone;
    stage.classList.toggle('is-failover', failover);
    setBackup(!any ? BACKUP_NONE : failover ? BACKUP_HOT : noReserve ? BACKUP_LONE : BACKUP_REST);
    if (srNow) {
      if (bad) srNow.textContent = t('sr_now.out');
      else if (!any) srNow.textContent = t('sr_now.none');
      else {
        const suffix = lone ? t('sr_now.suffix_lone') : anyDown ? t('sr_now.suffix_failover') : t('sr_now.suffix_ok');
        srNow.textContent = t('sr_now.active', { combo: comboText(activeConnections), total: TOTAL, capacity: sumCapacity.sr, combined: activeConnections.length >= 2 ? t('sr_now.combined') : '', drops: counted ? t('drops.one') : t('drops.none'), suffix });
      }
    }
    swapText(npSub, !any ? (anyDown ? t('np_sub.no_reserve') : t('np_sub.no_connection')) : lone ? t('np_sub.single_active') : anyDown ? t('np_sub.took_over') : t('np_sub.combines'), failover);
    swapText(gameSub, !any ? (anyDown ? t('game_sub.dropped') : t('game_sub.waiting')) : lone ? t('game_sub.no_reserve') : t('game_sub.continuous'));
    /* result row: the live figure is the combined capacity; the headline is the match's state */
    sumEl.textContent = any ? sumCapacity.n : '0';
    sumUnit.textContent = any ? sumCapacity.u : 'Mb/s';
    sumHeadline.textContent = bad ? t('sum.out') : !any ? t('sum.none') : lone ? (kicked ? t('sum.one_drop') : t('sum.no_reserve')) : t('sum.continuous');
    sumRow.classList.toggle('is-bad', bad);
    sumRow.classList.toggle('is-weak', lone);
    const first = activeConnections[0];
    const second = activeConnections[1];
    const pairNames = first && second ? t('sum_sub.pair_names', { first: nb(first.name), second: nb(second.name) }) : '';
    sumSub.innerHTML = resultNote ?? (!any || !first ? t('sum_sub.none') : activeConnections.length === 1 ? (kicked ? t('sum_sub.only_kicked', { name: nb(first.name), rest: alt(t('sum_sub.reset_count'), t('sum_sub.reset_count_short')) }) : t('sum_sub.only', { name: nb(first.name), rest: alt(t('sum_sub.add_reserve'), t('sum_sub.add_reserve_short')) })) : activeConnections.length === 2 ? alt(t('sum_sub.pair', { names: pairNames }), t('sum_sub.pair_short', { names: pairNames })) : alt(t('sum_sub.many', { count: activeConnections.length }), t('sum_sub.many_short', { count: activeConnections.length })));
    /* "Simular falha" works with a single connection too: it then plays the honest outcome */
    const canDrop = activeConnections.length >= 1 && !dropping && !(reduced.matches && anyDown && !any);
    dropButton.setAttribute('aria-disabled', canDrop ? 'false' : 'true');
    dropButton.title = dropping ? t('drop_title.wait') : reduced.matches ? (anyDown ? t('drop_title.reconnect') : t('drop_title.drop')) : activeConnections.length < 2 ? t('drop_title.drop_lone') : t('drop_title.drop');
    hintText.classList.toggle('is-done', hintGone && activeConnections.length >= 2);
    if (running) setRates();
  };

  const setPackets = (owner: Connection | Trunk): PacketSlot[] => {
    if ('id' in owner) return owner.segIn && owner.segOut ? [...owner.segIn.packets, ...owner.segOut.packets] : [];
    return owner.packets;
  };
  const setPlay = (owner: Connection | Trunk, on: boolean) => {
    const packets = setPackets(owner);
    clearLater(owner.offT);
    owner.offT = 0;
    if (on) {
      if (running) {
        packets.forEach((packet) => {
          if (packet.anim && packet.anim.playState !== 'running') packet.anim.play();
        });
      }
      return;
    }
    owner.offT = later(() => {
      owner.offT = 0;
      packets.forEach((packet) => packet.anim?.pause());
    }, 460);
  };
  /* the green claim is only made when there is something to fail over to */
  const stabilityHtml = () => {
    const activeConnections = active();
    if (!activeConnections.length) return `<b class="mi-stab is-off">${t('stab.none')}</b>`;
    if (activeConnections.length === 1) return `<b class="mi-stab is-off">${t('stab.lone')}</b>`;
    return `<b class="mi-stab">${t('stab.ok')}</b>`;
  };
  const say = (html: string, fade: boolean, raw?: boolean) => {
    const full = raw ? html : `${stabilityHtml()} · ${html}`;
    if (statusText.innerHTML === full) return;
    statusText.innerHTML = full;
    if (fade) pulse(statusText, 'is-swap');
  };
  const live = (message: string) => {
    liveEl.textContent = '';
    liveEl.textContent = message;
  };
  const setBackup = ([headline, sub]: [string, string]) => {
    if (backupSub.textContent === sub && backupHeadline.textContent === headline) return;
    backupHeadline.textContent = headline;
    backupSub.textContent = sub;
    pulse(backupSub, 'is-swap');
  };
  const burstNode = () => {
    if (reduced.matches) return;
    pulse(npIcon, 'is-burst');
    pulse(sumStrong, 'is-hit');
  };
  /* a card flipping between down and up snaps every colour for one painted frame */
  let snapRaf = 0;
  let snapTimer = 0;
  let snapElements: Element[] = [];
  const unsnap = () => {
    snapRaf = 0;
    clearLater(snapTimer);
    snapTimer = 0;
    const elements = snapElements;
    snapElements = [];
    elements.forEach((element) => element.classList.remove('is-snap'));
  };
  const snap = (elements: (Element | undefined)[]) => {
    elements.forEach((element) => {
      if (!element) return;
      element.classList.add('is-snap');
      snapElements.push(element);
    });
    if (snapRaf) return;
    snapRaf = nextFrame(() => {
      snapRaf = nextFrame(unsnap);
    });
    snapTimer = later(unsnap, 200);
  };

  /* --------------------------------------------------------------- actions */
  const turnOn = (id: string, burst: boolean) => {
    const connection = byId(id);
    if (!connection) return;
    connection.on = true;
    connection.down = false;
    resultNote = null;
    syncDerived();
    if (burst) pulse(connection.el, 'is-burst');
  };
  const turnOff = (id: string) => {
    const connection = byId(id);
    if (!connection) return;
    connection.on = false;
    connection.down = false;
    resultNote = null;
    connection.el.classList.remove('is-burst');
    syncDerived();
  };
  const dropConnection = (id: string) => {
    const connection = byId(id);
    if (!connection || !connection.on || connection.down) return null;
    connection.down = true;
    const rest = active();
    if (!rest.length) kicked = true;
    const [first, second] = rest;
    resultNote = !rest.length ? t('note.failed_alone', { name: nb(connection.name), rest: alt(t('note.out_long'), t('note.out_short')) }) : rest.length === 1 && first ? t('note.failed_single', { name: nb(connection.name), other: nb(first.name), rest: alt(t('note.instantly'), '') }) : rest.length > 2 ? t('note.failed_many', { name: nb(connection.name), count: rest.length }) : alt(t('note.failed_pair', { name: nb(connection.name), first: nb(first?.name ?? ''), second: nb(second?.name ?? '') }), t('note.failed_pair_short', { name: nb(connection.name) }));
    syncDerived();
    pulse(connection.el, 'is-shake');
    if (!active().length) {
      say(t('say.failed_alone', { name: nb(connection.name) }), true, true);
      return connection;
    }
    burstNode();
    say(t('say.failed', { name: nb(connection.name) }), true, true);
    return connection;
  };
  const dropSr = (connection: Connection) => {
    const others = active();
    if (!others.length) return t('live.failed_alone', { name: connection.sr });
    const via = others.length > 2 ? t('live.via_many', { count: others.length }) : joinList(others.map((other) => t('live.via_name', { name: other.sr })));
    return t('live.failed', { name: connection.sr, via });
  };
  const recoverSr = (connection: Connection) => {
    const activeConnections = active();
    if (activeConnections.length >= 2) return t('live.recovered', { name: connection.sr, combo: comboText(activeConnections) });
    return kicked ? t('live.recovered_kicked', { name: connection.sr }) : t('live.recovered_lone', { name: connection.sr });
  };
  const recoverConnection = (id: string) => {
    const connection = byId(id);
    if (!connection || !connection.down) return;
    const wasLone = !active().length;
    connection.down = false;
    connection.on = true;
    const activeConnections = active();
    resultNote = wasLone ? t('note.back_lone', { name: nb(connection.name), rest: alt(t('sum_sub.reset_count'), t('sum_sub.reset_count_short')) }) : t('note.back', { name: nb(connection.name), rest: alt(t('note.back_many', { count: activeConnections.length }), t('note.back_many_short', { count: activeConnections.length })) });
    syncDerived();
    pulse(connection.el, 'is-burst');
    say(wasLone ? t('say.back_lone', { name: nb(connection.name) }) : t('say.back', { name: nb(connection.name) }), true);
  };
  const setState = (map: Partial<Record<ConnectionId, boolean>>) => {
    connections.forEach((connection) => {
      connection.on = !!map[connection.id];
      connection.down = false;
      connection.el.classList.remove('is-burst');
    });
    resultNote = null;
    kicked = false;
    syncDerived();
  };

  /* ------------------------------------------------ auto demo (looping) */
  let lastDrop: string | null = null;
  let touched = false;
  let stepIndex = 1;
  let lastMove = 0;
  const nextReserve = () => connections.find((connection) => !connection.on && !connection.down) ?? null;
  const pickDrop = () => {
    const activeConnections = active();
    if (activeConnections.length < 2) return null;
    const sorted = [...activeConnections].sort((first, second) => second.mbps - first.mbps);
    return sorted[0]?.id === lastDrop && sorted.length > 1 ? (sorted[1] ?? null) : (sorted[0] ?? null);
  };
  const stepReset = () => {
    const extras = connections.filter((connection) => (connection.on || connection.down) && connection.id !== 'fibra' && connection.id !== 'wifi');
    setState({ fibra: true, wifi: true });
    if (extras.length) say(t('say.restart_extras', { names: joinList(extras.map((connection) => nb(connection.name))), count: extras.length }), true);
    else say(t('say.restart'), true);
  };
  const stepAdd = (second: boolean) => {
    const connection = nextReserve();
    if (!connection) {
      say(t('say.all_active'), true);
      return;
    }
    turnOn(connection.id, true);
    say(second ? t('say.added_more', { name: nb(connection.name) }) : t('say.added', { name: nb(connection.name) }), true);
  };
  const stepDrop = () => {
    const target = pickDrop();
    if (!target) {
      if (!touched) stepAdd(false);
      return;
    }
    lastDrop = target.id;
    dropConnection(target.id);
  };
  const stepRecover = () => {
    const fallen = connections.find((connection) => connection.down);
    if (fallen) recoverConnection(fallen.id);
  };
  /* scripted story (untouched visitor): pair -> +5G -> failure -> back -> +Cabo -> failure -> back -> restart */
  const STEPS: StoryStep[] = [
    { wait: 3600, run: stepReset },
    { wait: 3600, run: () => stepAdd(false) },
    { wait: 3900, run: stepDrop },
    { wait: 4200, run: stepRecover },
    { wait: 3600, run: () => stepAdd(true) },
    { wait: 3900, run: stepDrop },
    { wait: 4600, run: stepRecover },
  ];
  /* once the visitor has touched a card, the scripted add/remove beats stop for good; the failover beat
     keeps playing against the visitor's own set (never while fewer than two are active) */
  const STEPS_USER: StoryStep[] = [
    {
      wait: 4600,
      run: () => {
        if (active().length >= 2) stepDrop();
      },
    },
    { wait: 9500, run: stepRecover },
  ];
  const engaged = () => {
    let keyboard = false;
    try {
      keyboard = !!list.querySelector(':focus-visible');
    } catch {
      keyboard = list.contains(document.activeElement);
    }
    return keyboard || (!!hoverId && Date.now() - lastMove < 6000);
  };
  const canRun = () => built && visible && !document.hidden && !reduced.matches && !paused && !dropping && !engaged();
  const dormant = () => !visible || document.hidden || paused || reduced.matches;
  const schedule = (delay: number) => {
    clearLater(stepTimer);
    stepTimer = later(tickStep, delay);
  };
  const tickStep = () => {
    stepTimer = 0;
    if (!canRun()) {
      if (!dormant()) schedule(700);
      return;
    }
    const steps = touched ? STEPS_USER : STEPS;
    if (touched && stepIndex >= steps.length) stepIndex = 0;
    /* a fallen connection is always brought back before the next beat */
    if (touched && stepIndex === 0 && connections.some((connection) => connection.down)) stepIndex = 1;
    const step = steps[stepIndex] ?? steps[0];
    if (!step) return;
    step.run();
    stepIndex = (stepIndex + 1) % steps.length;
    schedule(step.wait);
  };
  const takeOver = () => {
    touched = true;
    stepIndex = 0;
    clearLater(stepTimer);
    stepTimer = 0;
    if (!dormant()) schedule(USER_IDLE);
  };

  /* ---------------------------------------------------------- interaction */
  const dismissHint = () => {
    if (hintGone) return;
    hintGone = true;
    if (active().length >= 2) {
      hintText.classList.add('is-done');
      flip(hintText);
    }
  };
  const loneMessage = (connection: Connection) => (kicked ? t('say.lone_kicked', { name: nb(connection.name) }) : t('say.lone', { name: nb(connection.name) }));
  const connectionButton = (event: Event) => (event.target instanceof Element ? event.target.closest<HTMLButtonElement>('[data-conn]') : null);

  listen(list, 'click', (event) => {
    const button = connectionButton(event);
    const connection = byId(button?.getAttribute('data-conn') ?? null);
    if (!connection) return;
    takeOver();
    dismissHint();
    if (connection.down) {
      recoverConnection(connection.id);
      live(recoverSr(connection));
      if (reduced.matches && !connections.some((other) => other.down)) dropLabel.textContent = t('drop');
    } else if (connection.on) {
      if (active().length === 1) {
        /* the last connection stays on: the showpiece never becomes a picture of a dead link */
        pulse(connection.el, 'is-nudge');
        say(t('say.keep_single'), true);
        live(t('live.keep_single'));
        return;
      }
      turnOff(connection.id);
      const activeConnections = active();
      const [only] = activeConnections;
      if (activeConnections.length === 1 && only) say(loneMessage(only), true);
      else say(t('say.to_reserve', { name: nb(connection.name) }), true);
      live(t('live.to_reserve', { name: connection.sr, rest: activeConnections.length === 1 && only ? t('live.only_name', { name: only.sr }) : `${comboText(activeConnections)}.` }));
    } else {
      turnOn(connection.id, true);
      const activeConnections = active();
      if (activeConnections.length === 1) say(loneMessage(connection), true);
      else say(t('say.joined', { name: nb(connection.name) }), true);
      live(t('live.joined', { name: connection.sr, rest: activeConnections.length === 1 ? t('live.single_active') : `${comboText(activeConnections)}.` }));
    }
    if (reduced.matches || !supportsMotion) drawStatic();
  });
  listen(list, 'pointerover', (event) => {
    const button = connectionButton(event);
    if (!button || event.pointerType === 'touch') return;
    hoverId = button.getAttribute('data-conn');
    lastMove = Date.now();
  });
  listen(list, 'pointermove', (event) => {
    if (event.pointerType !== 'touch') lastMove = Date.now();
  });
  listen(list, 'pointerout', (event) => {
    const button = connectionButton(event);
    if (!button || event.pointerType === 'touch') return;
    if (event.relatedTarget instanceof Node && button.contains(event.relatedTarget)) return;
    hoverId = null;
  });
  listen(dropButton, 'click', () => {
    if (dropButton.getAttribute('aria-disabled') === 'true') {
      /* a dimmed control still answers a tap: the reason lands in the visible status line */
      if (!dropping) say(t('say.activate_to_drop'), true);
      live(dropButton.title);
      return;
    }
    const activeConnections = active();
    const fastest = [...activeConnections].sort((first, second) => second.mbps - first.mbps)[0];
    if (reduced.matches) {
      /* no motion: the button swaps the two stills, the failover frame and the recovered frame */
      takeOver();
      dismissHint();
      const fallen = connections.find((connection) => connection.down);
      if (fallen) {
        recoverConnection(fallen.id);
        dropLabel.textContent = t('drop');
        live(recoverSr(fallen));
      } else if (fastest) {
        dropConnection(fastest.id);
        dropLabel.textContent = t('reconnect');
        live(dropSr(fastest));
      }
      drawStatic();
      return;
    }
    if (!fastest) return;
    takeOver();
    dismissHint();
    dropping = true;
    dropButton.classList.add('is-busy');
    dropButton.setAttribute('aria-busy', 'true');
    dropLabel.textContent = t('reconnecting');
    dropConnection(fastest.id);
    bridge.classList.add('is-hot');
    live(dropSr(fastest));
    if (!supportsMotion) drawStatic();
    clearLater(dropTimer);
    dropTimer = later(() => {
      dropping = false;
      recoverConnection(fastest.id);
      live(recoverSr(fastest));
      dropButton.classList.remove('is-busy');
      dropButton.setAttribute('aria-busy', 'false');
      dropLabel.textContent = t('drop');
      syncDerived();
      if (!supportsMotion) drawStatic();
      takeOver();
    }, 3400);
  });
  listen(pauseButton, 'click', () => {
    paused = !paused;
    root.classList.toggle('is-paused', paused);
    pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
    pauseButton.setAttribute('aria-label', paused ? t('resume_label') : t('pause_label'));
    const activeConnections = active();
    const anyDown = connections.some((connection) => connection.down);
    const [only] = activeConnections;
    const state = anyDown ? (activeConnections.length ? (activeConnections.length === 1 && only ? t('state.offline_single', { name: nb(only.name) }) : t('state.offline_many', { count: activeConnections.length })) : t('state.offline_none')) : activeConnections.length >= 2 ? t('state.combined', { combo: comboText(activeConnections) }) : activeConnections.length === 1 && only ? t('state.only', { name: nb(only.name) }) : t('state.none');
    if (paused) {
      stop();
      clearLater(stepTimer);
      stepTimer = 0;
      say(t('say.paused', { state }), false);
    } else {
      syncRun();
      const capital = state.charAt(0).toUpperCase() + state.slice(1);
      say(activeConnections.length >= 2 ? t('say.resumed_ok', { state: capital }) : activeConnections.length === 1 ? t('say.resumed_lone', { state: capital }) : t('say.resumed_none', { state: capital }), true);
    }
    live(paused ? t('live.paused') : t('live.resumed'));
  });
  listen(root, 'keydown', (event) => {
    const focused = document.activeElement;
    if (event.key === 'Escape' && focused instanceof HTMLElement && list.contains(focused)) focused.blur();
  });

  /* -------------------------------------------------------------- geometry */
  let busLeft: SVGPathElement | null = null;
  let busRight: SVGPathElement | null = null;
  const setTileMask = (width: number, height: number, boxes: Box[]) => {
    if (!tileMask.el || !tileMask.all) return;
    tileMask.el.setAttribute('x', '0');
    tileMask.el.setAttribute('y', '0');
    tileMask.el.setAttribute('width', String(round1(width)));
    tileMask.el.setAttribute('height', String(round1(height)));
    tileMask.all.setAttribute('width', String(round1(width)));
    tileMask.all.setAttribute('height', String(round1(height)));
    tileMask.rects.forEach((rect, index) => {
      const tile = boxes[index];
      if (tile) {
        rect.setAttribute('x', String(round1(tile.l)));
        rect.setAttribute('y', String(round1(tile.t)));
        rect.setAttribute('width', String(round1(tile.w)));
        rect.setAttribute('height', String(round1(tile.h)));
      } else {
        rect.setAttribute('width', '0');
        rect.setAttribute('height', '0');
      }
    });
  };
  const makeSet = (className: string) => {
    const set = document.createElement('div');
    set.className = `pk-set${className ? ` ${className}` : ''}`;
    packetLayer.appendChild(set);
    return set;
  };
  const makePackets = (set: HTMLDivElement, count: number): PacketSlot[] => {
    const packets: PacketSlot[] = [];
    for (let index = 0; index < count; index += 1) {
      const element = document.createElement('div');
      element.className = 'pk';
      set.appendChild(element);
      packets.push({ el: element, phase: (index + 0.15) / count, anim: null });
    }
    return packets;
  };
  const segment = (parks: number[]): Segment => ({ d: '', parks, packets: [], frames: null, parkIdx: null });
  const makePath = (className: string, parent: Element) => {
    const path = document.createElementNS(NS, 'path');
    if (className) path.setAttribute('class', className);
    parent.appendChild(path);
    return path;
  };
  const makeCircle = (radius: string, className: string, parent: Element) => {
    const circle = document.createElementNS(NS, 'circle');
    circle.setAttribute('r', radius);
    circle.setAttribute('class', className);
    parent.appendChild(circle);
    return circle;
  };
  const makeGroup = (className: string, parent?: Element) => {
    const group = document.createElementNS(NS, 'g');
    if (className) group.setAttribute('class', className);
    parent?.appendChild(group);
    return group;
  };

  const buildStage = () => {
    if (built) return;
    built = true;
    /* every wire layer is drawn through one mask that cuts out the node tiles (and, on phones, the six
       station tiles), so a glow can never bleed into a tile; the port dots sit outside the mask, on top */
    const defs = document.createElementNS(NS, 'defs');
    tileMask.el = document.createElementNS(NS, 'mask');
    tileMask.el.setAttribute('id', 'mi-tm');
    tileMask.el.setAttribute('class', 'w-tilemask');
    tileMask.el.setAttribute('maskUnits', 'userSpaceOnUse');
    tileMask.all = document.createElementNS(NS, 'rect');
    tileMask.all.setAttribute('class', 'w-maskall');
    tileMask.el.appendChild(tileMask.all);
    for (let index = 0; index < 9; index += 1) {
      const rect = document.createElementNS(NS, 'rect');
      rect.setAttribute('width', '0');
      rect.setAttribute('height', '0');
      tileMask.el.appendChild(rect);
      tileMask.rects.push(rect);
    }
    defs.appendChild(tileMask.el);
    svg.appendChild(defs);
    const wires = makeGroup('', svg);
    wires.setAttribute('mask', 'url(#mi-tm)');
    const portLayer = makeGroup('');
    const trunkGroup = makeGroup('w-trunkg', wires);
    trunk.base = makePath('w-base', trunkGroup);
    trunk.glow = makePath('w-glow', trunkGroup);
    trunk.hot = makePath('w-hot', makeGroup('w-hotwrap', trunkGroup));
    trunk.g = trunkGroup;
    busLeft = makePath('w-base w-bus', wires);
    busRight = makePath('w-base w-bus', wires);
    connections.forEach((connection) => {
      const group = makeGroup('', wires);
      group.setAttribute('data-wire', connection.id);
      const downWrap = makeGroup('w-downwrap', group);
      connection.baseIn = makePath('w-base w-in', downWrap);
      connection.baseOut = makePath('w-base w-out', downWrap);
      connection.baseOut.setAttribute('pathLength', '100');
      connection.cut = makeGroup('w-cut', downWrap);
      makeCircle('4.5', '', connection.cut).removeAttribute('class');
      makePath('', connection.cut).setAttribute('d', 'M-1.9,-1.9 L1.9,1.9 M1.9,-1.9 L-1.9,1.9');
      connection.glowIn = makePath('w-glow', group);
      connection.glowOut = makePath('w-glow', group);
      const hotWrap = makeGroup('w-hotwrap', group);
      connection.hotIn = makePath('w-hot', hotWrap);
      connection.hotOut = makePath('w-hot', hotWrap);
      connection.g = group;
      /* the card's two jack dots live outside the mask, in a twin group that mirrors the wire group's state */
      connection.pg = makeGroup('', portLayer);
      connection.portIn = makeCircle('2.75', 'w-rport w-rp-in', connection.pg);
      connection.portOut = makeCircle('2.75', 'w-rport w-rp-out', connection.pg);
      connection.set = makeSet('');
      connection.segIn = segment([0.38, 0.8]);
      connection.segIn.packets = makePackets(connection.set, 2);
      connection.segOut = segment([0.3, 0.6, 0.9]);
      connection.segOut.packets = makePackets(connection.set, 3);
    });
    trunk.set = makeSet('is-trunk');
    trunk.packets = makePackets(trunk.set, TRUNK_PHASES_BUS.length);
    (['pcOut', 'npIn', 'npOut', 'gameIn'] as const).forEach((key) => {
      ports[key] = makeCircle('4', 'w-port', portLayer);
    });
    svg.appendChild(portLayer);
    syncDerived();
    layout(true);
  };

  const setD = (path: SVGPathElement | undefined | null, d: string) => path?.setAttribute('d', d);
  const placeCut = (connection: Connection) => {
    if (connection.cutXY) connection.cut?.setAttribute('transform', connection.cutXY);
    connection.baseOut?.style.setProperty('--mi-cut-dash', '25 20 25 100');
    if (connection.baseOut) connection.baseOut.style.strokeDashoffset = '-25';
  };
  /* the out stroke is the live curve while the card is up and the straight severed stub while it is down */
  const syncStub = (connection: Connection) => {
    if (busMode) {
      placeStation(connection);
      return;
    }
    if (!connection.stubD || !connection.segOut) return;
    setD(connection.baseOut, connection.down ? connection.stubD : connection.segOut.d);
  };
  /* phone station: the spine runs unbroken and every card hangs off it by one short branch; a fallen
     card loses its branch while the spine sweeps past */
  const placeStation = (connection: Connection) => {
    const station = connection.busR;
    if (!station || !connection.baseIn || !connection.baseOut || !connection.cut || !connection.portIn || !connection.portOut) return;
    const { x } = station;
    const { cy } = station.card;
    const jackX = station.card.l - JACK_D;
    if (connection.down) {
      setD(connection.baseIn, '');
      connection.baseIn.style.strokeDasharray = '';
      setD(connection.glowIn, '');
      setD(connection.hotIn, '');
      setD(connection.baseOut, `M${round1(jackX)},${round1(cy)} L${round1(jackX - 9)},${round1(cy)}`);
      connection.baseOut.style.setProperty('--mi-cut-dash', '25 20 25 100');
      connection.baseOut.style.strokeDashoffset = '-25';
      connection.cut.setAttribute('transform', `translate(${round1(jackX - 13)},${round1(cy)})`);
      connection.cut.style.display = '';
      connection.portIn.setAttribute('cx', String(round1(jackX)));
      connection.portIn.setAttribute('cy', String(round1(cy)));
      connection.portOut.style.display = 'none';
      return;
    }
    const branch = `M${round1(x)},${round1(cy)} L${round1(jackX)},${round1(cy)}`;
    setD(connection.baseIn, branch);
    connection.baseIn.style.strokeDasharray = '';
    setD(connection.glowIn, branch);
    setD(connection.hotIn, branch);
    setD(connection.baseOut, '');
    connection.baseOut.style.setProperty('--mi-cut-dash', '30 35');
    connection.baseOut.style.strokeDashoffset = '0';
    connection.cut.style.display = 'none';
    connection.portIn.setAttribute('cx', String(round1(x)));
    connection.portIn.setAttribute('cy', String(round1(cy)));
    connection.portOut.style.display = '';
    connection.portOut.setAttribute('cx', String(round1(jackX)));
    connection.portOut.setAttribute('cy', String(round1(cy)));
  };
  /* phone bus: ONE unbroken vertical line, PC -> NoPing -> server */
  const busTrunk = (bus: NonNullable<typeof busGeometry>) => {
    const { x } = bus;
    return { d: `M${round1(x)},${round1(bus.pcB + 1)} L${round1(x)},${round1(bus.npT - 1)} M${round1(x)},${round1(bus.npB + 1)} L${round1(x)},${round1(bus.gmT - 1)}`, pk: `M${round1(x)},${round1(bus.pcB + 1)} L${round1(x)},${round1(bus.gmT - 1)}` };
  };
  const updateBusTrunk = () => {
    if (!busGeometry) return;
    const key = connections.map((connection) => (connection.down ? 1 : 0)).join('');
    if (key === busKey) return;
    busKey = key;
    const result = busTrunk(busGeometry);
    [trunk.base, trunk.glow, trunk.hot].forEach((path) => setD(path, result.d));
    trunk.d = result.pk;
    if (trunk.frames) setPath(trunk);
  };

  let lastKey = '';
  const nodeDy = new WeakMap<HTMLElement, number>();
  const layout = (force: boolean) => {
    if (!built) return;
    const stageRect = svg.getBoundingClientRect();
    if (!stageRect.width || !stageRect.height) return;
    const mobile = isMobile();
    /* every read first (one layout), then every write */
    const rects = connections.map((connection) => box(connection.el.getBoundingClientRect(), stageRect));
    const tiles = mobile ? connections.map((connection) => box(connection.ccEl.getBoundingClientRect(), stageRect)) : null;
    const nodes = [pcNode, npNode, gameNode];
    const icons = [pcIcon, npIcon, gameIcon];
    const nodeRects = nodes.map((node) => box(node.getBoundingClientRect(), stageRect));
    const iconRects = icons.map((icon) => box(icon.getBoundingClientRect(), stageRect));
    const firstRect = rects[0] as Box;
    const lastRect = rects[rects.length - 1] as Box;
    const centre = (firstRect.t + lastRect.b) / 2;
    /* all three node groups are centred on the card stack, so the three tiles sit on ONE axis */
    nodes.forEach((node, index) => {
      const current = nodeDy.get(node) ?? 0;
      const nodeRect = nodeRects[index] as Box;
      const dy = mobile ? 0 : centre - ((nodeRect.t + nodeRect.b) / 2 - current);
      if (Math.abs(dy - current) > 0.5) {
        node.style.setProperty('--mi-dy', `${round1(dy)}px`);
        nodeDy.set(node, dy);
        iconRects[index] = shiftBox(iconRects[index] as Box, dy - current);
      }
    });
    const [pcRect, npRect, gameRect] = iconRects as [Box, Box, Box];
    const key = [Math.round(stageRect.width), Math.round(stageRect.height), mobile ? 1 : 0, Math.round(firstRect.t), Math.round(lastRect.b), Math.round(pcRect.t), Math.round(pcRect.l), Math.round(npRect.t), Math.round(npRect.l), Math.round(gameRect.t), Math.round(gameRect.l), Math.round(firstRect.l)].join('|');
    if (!force && key === lastKey) return;
    lastKey = key;
    if (!pcRect.w || !npRect.w || !gameRect.w || rects.some((rect) => !rect.w || !rect.h)) return;
    svg.setAttribute('viewBox', `0 0 ${stageRect.width} ${stageRect.height}`);
    setTileMask(stageRect.width, stageRect.height, [pcRect, npRect, gameRect, ...(tiles ?? [])]);
    const modeChanged = busMode !== mobile;
    busMode = mobile;
    stage.classList.toggle('is-bus', mobile);
    trunk.live = mobile ? TRUNK_PHASES_BUS.length : TRUNK_PHASES_DESK.length;
    trunk.packets.forEach((packet, index) => {
      packet.phase = (mobile ? TRUNK_PHASES_BUS : TRUNK_PHASES_DESK)[index] ?? 0;
      packet.el.hidden = index >= trunk.live;
      /* a packet not shown in this mode has no animation at all */
      if ((modeChanged || packet.el.hidden) && packet.anim) {
        packet.anim.cancel();
        packet.anim = null;
      }
    });
    let d: string;
    let packetPath: string;
    if (!mobile) {
      /* every stroke docks INTO a port dot just outside the border it arrives at */
      const a = { x: pcRect.r + PORT_D, y: pcRect.cy };
      const b = { x: npRect.l - PORT_D, y: npRect.cy };
      connections.forEach((connection, index) => {
        const rect = rects[index] as Box;
        const dIn = `M${round1(a.x)},${round1(a.y)} ${cubicLeft(a.x, a.y, rect.l - JACK_D, rect.cy)}`;
        const dOut = `M${round1(rect.r + JACK_D)},${round1(rect.cy)} ${cubicRight(rect.r + JACK_D, rect.cy, b.x, b.y)}`;
        [connection.baseIn, connection.glowIn, connection.hotIn].forEach((path) => setD(path, dIn));
        [connection.glowOut, connection.hotOut].forEach((path) => setD(path, dOut));
        connection.portIn?.setAttribute('cx', String(round1(rect.l - JACK_D)));
        connection.portIn?.setAttribute('cy', String(round1(rect.cy)));
        connection.portOut?.setAttribute('cx', String(round1(rect.r + JACK_D)));
        connection.portOut?.setAttribute('cy', String(round1(rect.cy)));
        /* a fallen card's severed stub is a straight run on the card's own centreline */
        connection.stubD = `M${round1(rect.r + JACK_D)},${round1(rect.cy)} L${round1(rect.r + JACK_D + 12)},${round1(rect.cy)}`;
        connection.cutXY = `translate(${round1(rect.r + JACK_D + 17.5)},${round1(rect.cy)})`;
        setD(connection.baseOut, connection.down ? connection.stubD : dOut);
        if (connection.segIn) connection.segIn.d = dIn;
        if (connection.segOut) connection.segOut.d = dOut;
        if (connection.baseIn) connection.baseIn.style.strokeDasharray = '';
        if (connection.cut) connection.cut.style.display = '';
        if (connection.portOut) connection.portOut.style.display = '';
        connection.busR = null;
      });
      setD(busLeft, '');
      setD(busRight, '');
      /* NoPing -> server: the same curve vocabulary as the fans (a gentle rise), never a straight rule */
      const from = { x: npRect.r + PORT_D, y: npRect.cy };
      const to = { x: gameRect.l - PORT_D, y: gameRect.cy };
      const run = to.x - from.x;
      const rise = Math.min(12, run * 0.14);
      d = `M${round1(from.x)},${round1(from.y)} C${round1(from.x + run * 0.34)},${round1(from.y - rise)} ${round1(to.x - run * 0.34)},${round1(to.y - rise)} ${round1(to.x)},${round1(to.y)}`;
      packetPath = d;
      const portPositions: [keyof typeof ports, number, number][] = [
        ['pcOut', pcRect.r + PORT_D, pcRect.cy],
        ['npIn', npRect.l - PORT_D, npRect.cy],
        ['npOut', npRect.r + PORT_D, npRect.cy],
        ['gameIn', gameRect.l - PORT_D, gameRect.cy],
      ];
      portPositions.forEach(([name, x, y]) => {
        ports[name]?.setAttribute('cx', String(round1(x)));
        ports[name]?.setAttribute('cy', String(round1(y)));
      });
      trunk.hide = null;
      busGeometry = null;
      busKey = '';
    } else {
      const x = pcRect.cx;
      const tileRects = tiles ?? [];
      busGeometry = { x, pcB: pcRect.b, npT: npRect.t, npB: npRect.b, gmT: gameRect.t, tiles: tileRects, rects };
      busKey = connections.map((connection) => (connection.down ? 1 : 0)).join('');
      const bus = busTrunk(busGeometry);
      d = bus.d;
      packetPath = bus.pk;
      trunk.hide = [...tileRects, npRect].map((rect) => ({ l: rect.l - 1, t: rect.t - 1, r: rect.r + 1, b: rect.b + 1 }));
      connections.forEach((connection, index) => {
        connection.busR = { x, tile: tileRects[index] as Box, card: rects[index] as Box };
        [connection.glowIn, connection.hotIn, connection.glowOut, connection.hotOut].forEach((path) => setD(path, ''));
        if (connection.segIn) connection.segIn.d = '';
        if (connection.segOut) connection.segOut.d = '';
        placeStation(connection);
      });
      setD(busLeft, '');
      setD(busRight, '');
      const portPositions: [keyof typeof ports, number][] = [
        ['pcOut', pcRect.b + PORT_D],
        ['npIn', npRect.t - PORT_D],
        ['npOut', npRect.b + PORT_D],
        ['gameIn', gameRect.t - PORT_D],
      ];
      portPositions.forEach(([name, y]) => {
        ports[name]?.setAttribute('cx', String(round1(x)));
        ports[name]?.setAttribute('cy', String(round1(y)));
      });
    }
    [trunk.base, trunk.glow, trunk.hot].forEach((path) => setD(path, d));
    trunk.d = packetPath;
    svg.style.setProperty('--mi-cut-dash', cutDash(0.42));
    if (!mobile) connections.forEach(placeCut);
    if (modeChanged) {
      /* a mode switch retires the per-connection packet animations and re-derives which sets are shown */
      connections.forEach((connection) => {
        if (!connection.segIn || !connection.segOut) return;
        setPackets(connection).forEach((packet) => {
          if (packet.anim) {
            packet.anim.cancel();
            packet.anim = null;
          }
          packet.el.style.transform = '';
        });
        connection.segIn.frames = null;
        connection.segOut.frames = null;
      });
      syncDerived();
    }
    stage.classList.add('is-ready');
    if (motionArmed) scheduleMotion();
  };

  /* -------------------------------------------- packets (WAAPI along wires) */
  const allSegments = (): Segment[] => {
    const out: Segment[] = [];
    connections.forEach((connection) => {
      if (connection.segIn && connection.segOut) out.push(connection.segIn, connection.segOut);
    });
    out.push(trunk);
    return out;
  };
  const sampleFrames = (d: string, parks: number[], hide: HideRect[] | null) => {
    const poly = samplePath(d);
    const length = poly.len;
    if (!length) return null;
    const count = Math.max(24, Math.min(200, Math.round(length / 6)));
    const points: { x: number; y: number }[] = [];
    for (let index = 0; index <= count; index += 1) points.push(pointAt(poly, (length * index) / count));
    const keyframes: Keyframe[] = [];
    const visibleAt: boolean[] = [];
    let previousAngle = 0;
    for (let index = 0; index <= count; index += 1) {
      const before = points[Math.max(0, index - 1)] as { x: number; y: number };
      const after = points[Math.min(count, index + 1)] as { x: number; y: number };
      const point = points[index] as { x: number; y: number };
      let angle = (Math.atan2(after.y - before.y, after.x - before.x) * 180) / Math.PI;
      if (index) {
        while (angle - previousAngle > 180) angle -= 360;
        while (angle - previousAngle < -180) angle += 360;
      }
      previousAngle = angle;
      const frame: Keyframe = { offset: index / count, transform: `translate(${round1(point.x)}px,${round1(point.y)}px) rotate(${round1(angle)}deg)` };
      const shown = !hide || !inRects(point, hide);
      if (hide) frame.opacity = shown ? 1 : 0;
      visibleAt.push(shown);
      keyframes.push(frame);
    }
    const parkIdx = parks.map((park) => {
      let index = Math.max(0, Math.min(count, Math.round(park * count)));
      let guard = 0;
      /* a parked (static) packet always rests on a visible stretch */
      while (!visibleAt[index] && guard < count) {
        guard += 1;
        index = Math.min(count, index + 1);
      }
      return index;
    });
    return { frames: keyframes, parkIdx, len: length };
  };
  const makeAnimation = (packet: PacketSlot, keyframes: Keyframe[]) => {
    packet.anim = packet.el.animate(keyframes, { duration: BASE_MS, iterations: Infinity, easing: 'linear', delay: -packet.phase * BASE_MS });
  };
  const setPath = (owner: Segment) => {
    if (!owner.d) return;
    const result = sampleFrames(owner.d, owner.parks, owner.hide ?? null);
    if (!result) return;
    owner.frames = result.frames;
    owner.parkIdx = result.parkIdx;
    owner.len = result.len;
    owner.packets.forEach((packet, index) => {
      if (packet.el.hidden) return;
      if (packet.anim) {
        try {
          (packet.anim.effect as KeyframeEffect | null)?.setKeyframes(result.frames);
        } catch {
          const time = packet.anim.currentTime;
          packet.anim.cancel();
          packet.anim = null;
          makeAnimation(packet, result.frames);
          if (packet.anim && time !== null) (packet.anim as Animation).currentTime = time;
        }
      } else if (!running) {
        const park = result.frames[result.parkIdx[index % result.parkIdx.length] ?? 0];
        packet.el.style.transform = String(park?.transform ?? '');
      }
    });
  };
  let motionArmed = false;
  let motionQueue: Segment[] = [];
  let motionRaf = 0;
  const scheduleMotion = () => {
    if (!built) return;
    motionQueue = allSegments();
    if (motionRaf) cancelFrame(motionRaf);
    motionRaf = nextFrame(motionStep);
  };
  const motionStep = () => {
    const next = motionQueue.shift();
    if (next) setPath(next);
    if (motionQueue.length) {
      motionRaf = nextFrame(motionStep);
      return;
    }
    motionRaf = 0;
    stage.classList.add('is-motion');
    if (reduced.matches || !supportsMotion) drawStatic();
    else syncRun();
  };
  const armMotion = () => {
    if (motionArmed) return;
    buildStage();
    motionArmed = true;
    if (stage.classList.contains('is-ready')) scheduleMotion();
  };
  const ensureAnimations = (owner: Segment) => {
    if (!owner.frames) return;
    owner.packets.forEach((packet) => {
      if (packet.anim || packet.el.hidden) return;
      packet.el.style.transform = '';
      makeAnimation(packet, owner.frames as Keyframe[]);
    });
  };
  const setRates = () => {
    connections.forEach((connection) => {
      if (!connection.segIn) return;
      const rate = rateFor(connection);
      setPackets(connection).forEach((packet) => {
        if (packet.anim) packet.anim.playbackRate = rate;
      });
    });
    /* the trunk moves at a steady ~170 px/s whatever its length, clamped so it never races or crawls */
    const trunkRate = trunk.len ? Math.max(0.5, Math.min(2.2, ((BASE_MS / 1000) * 170) / trunk.len)) : 1;
    trunk.packets.forEach((packet) => {
      if (packet.anim) packet.anim.playbackRate = trunkRate * boost;
    });
  };
  const eachAnimation = (callback: (animation: Animation) => void) => {
    allSegments().forEach((owner) => {
      owner.packets.forEach((packet) => {
        if (packet.anim) callback(packet.anim);
      });
    });
  };
  const start = () => {
    if (reduced.matches || paused || !supportsMotion || !built) return;
    running = true;
    root.classList.add('is-live');
    allSegments().forEach(ensureAnimations);
    setRates();
    /* only the sets in the combo run; hidden sets stay paused */
    connections.forEach((connection) => {
      const on = connection.on && !connection.down;
      setPackets(connection).forEach((packet) => {
        if (!packet.anim) return;
        if (on) {
          if (packet.anim.playState !== 'running') packet.anim.play();
        } else packet.anim.pause();
      });
    });
    const anyOn = active().length > 0;
    trunk.packets.forEach((packet) => {
      if (!packet.anim) return;
      if (anyOn) {
        if (packet.anim.playState !== 'running') packet.anim.play();
      } else packet.anim.pause();
    });
    sparksRun(true);
  };
  const stop = () => {
    running = false;
    root.classList.remove('is-live');
    sparksRun(false);
    eachAnimation((animation) => animation.pause());
  };
  const drawStatic = () => {
    running = false;
    root.classList.remove('is-live');
    sparksRun(false);
    allSegments().forEach((owner) => {
      if (!owner.frames) return;
      owner.packets.forEach((packet, index) => {
        if (packet.anim) {
          packet.anim.cancel();
          packet.anim = null;
        }
        const parkIndex = owner.parkIdx ?? [0];
        const park = owner.frames?.[parkIndex[index % parkIndex.length] ?? 0];
        packet.el.style.transform = String(park?.transform ?? '');
      });
    });
  };

  const syncRun = () => {
    const on = visible && !document.hidden;
    root.classList.toggle('is-idle', !on);
    if (on) {
      armMotion();
      start();
      if (!stepTimer && !paused && !reduced.matches) schedule(touched ? USER_IDLE : 600);
    } else {
      stop();
      clearLater(stepTimer);
      stepTimer = 0;
    }
  };
  if (typeof IntersectionObserver === 'function') {
    const buildObserver = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0]?.isIntersecting) {
          buildStage();
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px' }
    );
    buildObserver.observe(stage);
    const visibleObserver = new IntersectionObserver(
      (entries) => {
        visible = !!entries[0]?.isIntersecting;
        syncRun();
      },
      { threshold: 0.05 }
    );
    visibleObserver.observe(stage);
    runtime.onDispose(() => {
      buildObserver.disconnect();
      visibleObserver.disconnect();
    });
    later(() => {
      if (built || document.hidden) return;
      const rect = stage.getBoundingClientRect();
      if (rect.bottom > -320 && rect.top < window.innerHeight + 320) buildStage();
    }, 1500);
  } else {
    visible = true;
    armMotion();
  }
  const onVisibility = () => syncRun();
  document.addEventListener('visibilitychange', onVisibility);
  runtime.onDispose(() => document.removeEventListener('visibilitychange', onVisibility));
  let resizeRaf = 0;
  const scheduleLayout = () => {
    if (resizeRaf) cancelFrame(resizeRaf);
    resizeRaf = nextFrame(() => layout(false));
  };
  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(scheduleLayout);
    resizeObserver.observe(stage);
    runtime.onDispose(() => resizeObserver.disconnect());
  } else {
    window.addEventListener('resize', scheduleLayout);
    runtime.onDispose(() => window.removeEventListener('resize', scheduleLayout));
  }
  document.fonts?.ready.then(() => {
    if (runtime.isAlive()) scheduleLayout();
  });
  const onReducedChange = () => {
    if (reduced.matches) {
      stop();
      drawStatic();
    } else syncRun();
  };
  reduced.addEventListener('change', onReducedChange);
  runtime.onDispose(() => reduced.removeEventListener('change', onReducedChange));

  /* the providers follow the same geolocation answer as the Multiconexão card (one request per page) */
  if (!forced) {
    requestVisitorGeo().then((answer) => {
      const normalized = normalizeGeo(answer);
      if (!runtime.isAlive() || !normalized) return;
      applyProviders(pickProviders(normalized.country, normalized.isp));
      paintConnections();
    });
  }

  /* reduced motion rests on the confident still (two connections summed, "Conexão estável", zero drops);
     "Simular falha" then swaps to the failover still and back */
  syncDerived();
  if (!reduced.matches) schedule(STEPS[0]?.wait ?? 3600);

  return () => {
    runtime.dispose();
    eachAnimation((animation) => animation.cancel());
    packetLayer.replaceChildren();
    svg.replaceChildren();
    root.classList.remove('is-js', 'is-live', 'is-idle', 'is-paused', 'is-offline', 'is-weak');
  };
}
